import React, { ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import CountrySelector from './CountrySelector'
import { simpleEmailValidation } from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import { TRAVEL_RULES_COMPLIANCE_BANNER } from '../util/BoilerPlate'
import { PrimaryButton } from './Button'
import {
  emptyTravelRuleFields,
  TravelRuleFields,
  TravelRuleFieldsCollected,
  CheckoutType
} from './Checkout'
import {
  TextInputWithLabelWithSuggestions,
  InputLabel,
  TextInput
} from './Input'
import Switch from './Switch'
import Radom, { Profile } from '../api/Radom'
import _ from 'lodash'
import { Country } from 'country-state-city'
import Cookies from 'js-cookie'
const COOKIE_KEY = 'radom_travel_rule_fields'

// ---------------------------------------------------------------------------------------------------------------------
const AddressRow = styled.div`
  display: flex;
  gap: 10px;
`

const TravelRuleFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: auto;
  font-size: 14px;

  @media (max-width: 900px) {
    width: 95%;
  }

  @media (min-width: 901px) {
    width: 420px;
  }
`

const TravelRuleFieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

// ---------------------------------------------------------------------------------------------------------------------
interface IProps {
  travelRuleFieldsCollected: TravelRuleFieldsCollected
  accountType?: string
  onTravelRuleFieldsComplete: (trFields: TravelRuleFields) => void
  prefilledData?: Profile
}
const labelText: Record<string, [string, string]> = {
  name: ['Name', 'Representative Name'],
  email: ['Email Address', 'Email Address'],
  billingAddress: ['Billing Address', 'Company Billing Address'],
  country: ['Country', 'Company Registration Country'],
  company: ['', 'Company Name']
}
const fetchLabelText = (labelName: string, isBusiness: boolean): string =>
  labelText[labelName][+isBusiness]

function generateInputLabel(
  labelName: string,
  isBusiness: boolean
): ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: '15px'
      }}
    >
      <InputLabel>{fetchLabelText(labelName, isBusiness)}</InputLabel>
    </div>
  )
}

const getStoredFields = (): TravelRuleFields | null => {
  const stored = Cookies.get(COOKIE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch (e) {
      console.error('Error parsing stored travel rule fields:', e)
    }
  }
  return null
}
// ---------------------------------------------------------------------------------------------------------------------
const TravelRuleFieldsWidget = (props: IProps): React.ReactElement => {
  const storedFields = getStoredFields()
  const [isManuallyEditing, setIsManuallyEditing] = useState(false)
  const [isBusiness, setIsBusiness] = useState(() => {
    const storedFields = getStoredFields()

    if (storedFields?.accountType) {
      return storedFields.accountType === CheckoutType.Business
    }

    const initialAccountType = props.travelRuleFieldsCollected.accountType ?? props.accountType
    if (initialAccountType) {
      return initialAccountType === 'Business'
    }

    return !!props.prefilledData?.companyName
  })

  const [collectedTravelRuleFields, setCollectedTravelRuleFields] =
    useState<TravelRuleFields>(() => {
      if (storedFields) {
        return storedFields
      }

      const baseFields = emptyTravelRuleFields()

      if (props.prefilledData) {
        if (props.prefilledData.name) baseFields.name = props.prefilledData.name
        if (props.prefilledData.email) baseFields.emailAddress = props.prefilledData.email
        if (props.prefilledData.companyName) baseFields.companyName = props.prefilledData.companyName
        if (props.prefilledData.residenceCountry) baseFields.countryOfResidence = props.prefilledData.residenceCountry
      }

      return baseFields
    })

  const [errorMessage, setErrorMessage] = useState<string>()
  const [addressSuggestionList, setAddressSuggestionList] = useState<string[]>(
    []
  )
  const [inputAddress, setInputAddress] = useState<string>()
  const [showSuggestionsAddressInput, setShowSuggestionsAddressInput] =
    useState(true)
  const collectedTravelRuleFieldsRef = useRef<TravelRuleFields>(
    collectedTravelRuleFields
  )

  const addressBlockRequired =
    !props.travelRuleFieldsCollected.dateOfBirthCollected &&
    (!props.travelRuleFieldsCollected.addressLine1Collected ||
      !props.travelRuleFieldsCollected.townOrCityCollected ||
      !props.travelRuleFieldsCollected.postalCodeCollected ||
      !props.travelRuleFieldsCollected.stateOrProvinceCollected)

  const updateTRFAndCache = (newFields: TravelRuleFields): void => {
    collectedTravelRuleFieldsRef.current = newFields
    setCollectedTravelRuleFields(newFields)
    Cookies.set(COOKIE_KEY, JSON.stringify(newFields), { expires: 7 })
  }

  const setTRFsFromHtmlElement = (
    inputEl: any,
    trfName: string,
    modifierFn?: (val: string) => string
  ): void => {
    const newTrFields = { ...collectedTravelRuleFields }
    const inputValue = (inputEl.target as HTMLInputElement).value
    newTrFields[trfName] = modifierFn ? modifierFn(inputValue) : inputValue
    updateTRFAndCache(newTrFields)
  }

  const toggleIsBusiness = (): void => {
    const newIsBusinessState = !isBusiness
    setIsBusiness(newIsBusinessState)
    setIsManuallyEditing(true)

    const newFields = {
      ...collectedTravelRuleFields,
      companyName: newIsBusinessState ? collectedTravelRuleFields.companyName : '',
      accountType: newIsBusinessState ? CheckoutType.Business : CheckoutType.Individual
    }

    updateTRFAndCache(newFields)
  }

  const debouncedFetch = useRef(
    _.debounce(
      (value: string, countryCode: string, setAddressSuggestionList) => {
        Radom.getGoolgePlaceAutoComplete(value, countryCode).then((res) => {
          setAddressSuggestionList(res)
        })
      },
      500
    )
  ).current

  const handleClickAddressSuggestion = (address: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [addressLine1, city, country] = address
      .split(',')
      .map((part) => part.trim())
    setShowSuggestionsAddressInput(false)
    updateTRFAndCache({
      ...collectedTravelRuleFields,
      addressLine1,
      townOrCity: city
    })
  }

  useEffect(() => {
    if (
      collectedTravelRuleFields.addressLine1 &&
      collectedTravelRuleFields.countryOfResidence
    ) {
      setShowSuggestionsAddressInput(false)
    }
  }, [collectedTravelRuleFields])

  return (
    <TravelRuleFieldsContainer>
      {errorMessage && errorMessage.length > 0 && (
        <div
          style={{
            backgroundColor: RADOM_COLORS.ERROR,
            padding: 15,
            marginBottom: 10,
            borderRadius: 5,
            fontSize: 14,
            color: 'white',
            overflowWrap: 'break-word'
          }}
        >
          {errorMessage}
        </div>
      )}

      <div
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          marginTop: 20
        }}
      >
        <Switch
          checked={isBusiness}
          onClick={toggleIsBusiness}
        />
        <div
          style={{
            flex: 1,
            color: RADOM_COLORS.BLACK,
            fontSize: 14,
            overflowWrap: 'break-word',
            marginLeft: 10
          }}
        >
          Paying as a business?
        </div>
      </div>

      {isBusiness && (
        <TravelRuleFieldsRow key="missingCompanyName">
          {generateInputLabel('company', true)}
          <TextInput
            key="trCompanyName"
            name="organization"
            autoComplete="organization"
            disabled={!!props.travelRuleFieldsCollected.companyNameCollected && !isManuallyEditing}
            value={collectedTravelRuleFields.companyName}
            placeholder={'Enter your company name'}
            required={true}
            onInput={(el) =>
              updateTRFAndCache({
                ...collectedTravelRuleFields,
                companyName: el.currentTarget.value
              })
            }
          />
        </TravelRuleFieldsRow>
      )}

      <TravelRuleFieldsRow key="missingName">
        {generateInputLabel('name', isBusiness)}
        <TextInput
          style={{
            borderRadius: 5
          }}
          key="trName"
          name="name"
          autoComplete="name"
          value={collectedTravelRuleFields.name}
          placeholder={'Enter name'}
          disabled={!!props.travelRuleFieldsCollected.nameCollected}
          required={true}
          onInput={(el) => {
            updateTRFAndCache({
              ...collectedTravelRuleFields,
              name: el.currentTarget.value
            })
            setCollectedTravelRuleFields({
              ...collectedTravelRuleFields,
              name: el.currentTarget.value
            })
          }}
        />
      </TravelRuleFieldsRow>

      <TravelRuleFieldsRow key="missingEmailAddress">
        {generateInputLabel('email', isBusiness)}
        <TextInput
          style={{
            borderRadius: 5
          }}
          key="trEmailAddress"
          name="email"
          autoComplete="email"
          disabled={!!props.travelRuleFieldsCollected.emailAddressCollected}
          value={collectedTravelRuleFields.emailAddress}
          placeholder={'Enter email address'}
          required={true}
          onInput={(el) => {
            updateTRFAndCache({
              ...collectedTravelRuleFields,
              emailAddress: el.currentTarget.value
            })
            setCollectedTravelRuleFields({
              ...collectedTravelRuleFields,
              emailAddress: el.currentTarget.value.toLowerCase()
            })
          }}
        />
      </TravelRuleFieldsRow>

      {generateInputLabel('billingAddress', isBusiness)}
      <TravelRuleFieldsRow
        key="missingCountryOfResidence"
        style={{ margin: '3px 0px' }}
      >
        <CountrySelector
          selectedCountry={collectedTravelRuleFields.countryOfResidence}
          onCountrySelected={(country) => {
            if (country) {
              updateTRFAndCache({
                ...collectedTravelRuleFields,
                countryOfResidence: country.name
              })
            }
          }}
        />
      </TravelRuleFieldsRow>

      {collectedTravelRuleFields.countryOfResidence && (
        <>
          {showSuggestionsAddressInput && (
            <>
              <TextInputWithLabelWithSuggestions
                label=""
                inputStyle={{
                  borderRadius: 5
                }}
                placeholder="Enter address"
                value={inputAddress ?? ''}
                required={true}
                disabled={collectedTravelRuleFields.countryOfResidence === ''}
                suggestionList={addressSuggestionList}
                setSelectedItems={(items) => {
                  setAddressSuggestionList([])
                  handleClickAddressSuggestion(items[0])
                }}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value
                  if (value.length === 0) {
                    setAddressSuggestionList([])
                  }
                  if (
                    value.length > 5 &&
                    collectedTravelRuleFields.countryOfResidence
                  ) {
                    const country = Country.getAllCountries().find(
                      (c) =>
                        c.name === collectedTravelRuleFields.countryOfResidence
                    )
                    if (country) {
                      debouncedFetch(
                        value,
                        country.isoCode,
                        setAddressSuggestionList
                      )
                    }
                  }
                  setInputAddress(value)
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: RADOM_COLORS.GRAY4,
                  marginTop: 5,
                  marginBottom: 10,
                  width: '100%',
                  textAlign: 'right',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setShowSuggestionsAddressInput(false)
                  setInputAddress('')
                }}
              >
                Can not find your address?
              </span>
            </>
          )}

          {!showSuggestionsAddressInput && (
            <>
              {
                // Address line 1
                addressBlockRequired &&
                  !props.travelRuleFieldsCollected.addressLine1Collected && (
                  <TextInput
                    key="trAddressLine1"
                    name="address-line1"
                    autoComplete="address-line1"
                    placeholder="Address line 1 (required)"
                    onInput={(el) =>
                      setTRFsFromHtmlElement(el, 'addressLine1')
                    }
                    style={{ margin: '3px 0px' }}
                    value={collectedTravelRuleFields.addressLine1}
                  />
                )
              }

              {
                // Address line 2
                addressBlockRequired &&
                  !props.travelRuleFieldsCollected.addressLine2Collected && (
                  <TextInput
                    key="trAddressLine2"
                    name="address-line2"
                    autoComplete="address-line2"
                    placeholder="Address line 2"
                    onInput={(el) =>
                      setTRFsFromHtmlElement(el, 'addressLine2')
                    }
                    style={{ margin: '3px 0px' }}
                    value={collectedTravelRuleFields.addressLine2}
                  />
                )
              }

              {
                // Town/City or Postal Code
                addressBlockRequired &&
                  (!props.travelRuleFieldsCollected.townOrCityCollected ||
                    !props.travelRuleFieldsCollected.postalCodeCollected) && (
                  <AddressRow>
                    <>
                      {addressBlockRequired &&
                          !props.travelRuleFieldsCollected
                            .townOrCityCollected && (
                        <TextInput
                          key="trTownOrCity"
                          name="address-level2"
                          autoComplete="address-level2"
                          placeholder="Town/City (required)"
                          onInput={(el) =>
                            setTRFsFromHtmlElement(el, 'townOrCity')
                          }
                          value={collectedTravelRuleFields.townOrCity}
                          style={{ margin: '3px 0px', width: '50%' }}
                        />
                      )}

                      {addressBlockRequired &&
                          !props.travelRuleFieldsCollected
                            .postalCodeCollected && (
                        <TextInput
                          key="trPostalCode"
                          name="postal-code"
                          autoComplete="postal-code"
                          placeholder="Postal Code/Zip"
                          onInput={(el) =>
                            setTRFsFromHtmlElement(el, 'postalCode')
                          }
                          style={{ margin: '3px 0px', width: '50%' }}
                          value={collectedTravelRuleFields.postalCode}
                        />
                      )}
                    </>
                  </AddressRow>
                )
              }

              {
                // State/Province
                addressBlockRequired &&
                  !props.travelRuleFieldsCollected.stateOrProvinceCollected && (
                  <TextInput
                    key="trStateOrProvince"
                    name="address-level1"
                    autoComplete="address-level1"
                    placeholder="State/Province"
                    onInput={(el) =>
                      setTRFsFromHtmlElement(el, 'stateOrProvince')
                    }
                    style={{ margin: '3px 0px' }}
                    value={collectedTravelRuleFields.stateOrProvince}
                  />
                )
              }
            </>
          )}
        </>
      )}

      {TRAVEL_RULES_COMPLIANCE_BANNER}

      <PrimaryButton
        style={{
          background: RADOM_COLORS.BLUE4,
          width: '100%',
          marginTop: '25px'
        }}
        onClick={() => {
          const latestFields = { ...collectedTravelRuleFieldsRef.current }

          latestFields.accountType = isBusiness
            ? CheckoutType.Business
            : CheckoutType.Individual

          let missingField: string | undefined
          if (
            isBusiness &&
            !props.travelRuleFieldsCollected.companyNameCollected &&
            !latestFields.companyName
          ) {
            missingField = 'company name'
          } else if (
            !props.travelRuleFieldsCollected.nameCollected &&
            !latestFields.name
          ) {
            missingField = 'name'
          } else if (
            !props.travelRuleFieldsCollected.emailAddressCollected &&
            (!latestFields.emailAddress ||
              !simpleEmailValidation(latestFields.emailAddress))
          ) {
            missingField = 'email address'
          } else if (!latestFields.addressLine1) {
            missingField = 'address'
          } else if (!latestFields.townOrCity) {
            missingField = 'city'
          } else if (
            !props.travelRuleFieldsCollected.countryOfResidenceCollected &&
            !latestFields.countryOfResidence
          ) {
            missingField = 'country of residence'
          }

          if (missingField) {
            setErrorMessage(`Please enter ${missingField}.`)
          } else {
            props.onTravelRuleFieldsComplete(latestFields)
            setErrorMessage('')
          }
        }}
      >
        Continue
      </PrimaryButton>
    </TravelRuleFieldsContainer>
  )
}

export default TravelRuleFieldsWidget
